import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { LoadingStatuses } from '@interfaces/util';
import { BottomPopupService } from '@services/bottom-popup.service';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

@Component({
	selector: 'app-mobility-image-upload',
	templateUrl: './mobility-image-upload.component.html',
	styleUrls: ['./mobility-image-upload.component.scss'],
})
export class MobilityImageUploadComponent implements OnInit, OnDestroy {
	private _bottomPopup = inject(BottomPopupService);
	private sanitizer = inject(DomSanitizer);
	constructor() {}

	@Input() index: number;

	@Input() imagePrefix: string = '';

	@Input() uploadImageToServerCallback: (file: File) => Observable<Object>;

	@Output() readonly finished = new EventEmitter<File>();

	@Output() readonly startedUploading = new EventEmitter<void>();

	image$ = new BehaviorSubject<{ file: File | null; preview: SafeUrl }>({ file: null, preview: '' });

	status$ = new BehaviorSubject<LoadingStatuses>('idle');

	destroy$ = new Subject<void>();

	ngOnInit(): void {
		if (!this.uploadImageToServerCallback) {
			throw new Error('uploadImageToServerCallback is required');
		}

		this.status$.pipe(takeUntil(this.destroy$)).subscribe(status => {
			if (status === 'success') {
				this.finished.emit(this.image$.value.file);
			}
		});
	}

	ngOnDestroy(): void {
		if (this.image$.value.preview) {
			URL.revokeObjectURL(this.image$.value.preview as string);
		}
		this.destroy$.next();
		this.destroy$.complete();
	}

	onFileSelected(event: Event) {
		if (!(event.target instanceof HTMLInputElement)) {
			throw new Error('event.target is not an HTMLInputElement');
		}
		const input = event.target;
		if (!input.files?.length) {
			return;
		}
		let file = input.files[0];
		if (!ALLOWED_TYPES.includes(file.type)) {
			this.status$.next('error');
			this._bottomPopup.show('סוג תמונה אינו נתמך');
			return;
		}

		file = new File([file], `${this.imagePrefix}-${this.index + 1}.jpg`, { type: file.type });
		this.handleReadingAndUpload(file);
	}

	handleReadingAndUpload(file: File) {
		const reader = new FileReader();
		if (file.size > 10_000_000) {
			this._bottomPopup.show('התמונה גדולה מדי, נא להעלות תמונה קטנה יותר');
			return;
		}
		reader.onload = (e: ProgressEvent<FileReader>) => {
			const preview: string = typeof e.target.result === 'string' ? e.target.result : URL.createObjectURL(file);

			this.status$.next('loading');
			this.uploadImageToServer(file)
				.pipe(takeUntil(this.destroy$))
				.subscribe({
					next: () => {
						const safePreview: SafeUrl = this.sanitizer.sanitize(SecurityContext.URL, preview);
						this.image$.next({ file, preview: safePreview });
						this.status$.next('success');
						this._bottomPopup.show('תמונה עלתה בהצלחה!');
					},
					error: err => {
						this.markAsError();
						console.error(err);
						URL.revokeObjectURL(preview);
					},
				});
		};
		reader.readAsDataURL(file);
		this.status$.next('idle');
	}

	markAsError() {
		this.status$.next(this.image$.value.file ? 'idle' : 'error');

		this._bottomPopup.show('העלאת תמונה נכשלה');
	}

	uploadImageToServer(file: File) {
		if (!file) {
			throw new Error('no file');
		}

		return this.uploadImageToServerCallback(file);
	}
}
